import { PageHeader } from '@/shell/AppShell'
import { ErrorState, EmptyState } from '@/components/States'
import { fetchMigrations, fetchQueue, fetchRedis, type MigrationRow, type QueueStats, type RedisStats } from './infraApi'
import { useLoad, RefreshBtn, Rows, Stat } from './infraShared'
import { fetchSlowQueries, fetchApiMetrics, IX, type SlowQueriesResp, type ApiMetrics } from './infraExtApi'

function fmtMs(ms: number): string {
  if (ms < 1) return `${(ms * 1000).toFixed(0)} µs`
  if (ms < 1000) return `${ms.toFixed(1)} ms`
  return `${(ms / 1000).toFixed(2)} s`
}

/* ── A17.06 — Migratsiya boshqaruvi (schema_migrations) ────────── */
export function InfraMigrationControlScreen() {
  const { data, err, reload } = useLoad<MigrationRow[]>(fetchMigrations)
  const applied = data?.filter((m) => m.applied) ?? []
  const pending = data?.filter((m) => !m.applied) ?? []
  const current = applied[0]?.version // newest-first → first applied is current
  return (
    <>
      <PageHeader title={IX.migControl} screenId="A17.06" description={IX.migControlDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows />
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            <div className="grid grid-cols-3 gap-2.5">
              <Stat label={IX.currentVersion} value={current || '—'} />
              <Stat label={IX.applied} value={String(applied.length)} tone="success" />
              <Stat label={IX.pending} value={String(pending.length)} tone={pending.length > 0 ? 'warning' : undefined} />
            </div>
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.version}</th>
                    <th className="px-4 py-2 font-medium">{IX.migration}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((m) => (
                    <tr key={m.version} className={`border-t border-border-subtle ${m.version === current ? 'bg-accent/5' : ''}`}>
                      <td className="px-4 py-2 font-mono text-secondary">
                        {m.version}
                        {m.version === current && <span className="ml-2 rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent">{IX.current}</span>}
                      </td>
                      <td className="px-4 py-2 text-primary">{m.name || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${m.applied ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                          {m.applied ? IX.applied : IX.pending}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.07 — Navbatlar (Horizon → MyGram Redis navbatlari) ──────── */
export function InfraQueuesScreen() {
  const q = useLoad<QueueStats>(fetchQueue)
  const r = useLoad<RedisStats>(fetchRedis)
  const reload = () => {
    q.reload()
    r.reload()
  }
  const err = q.err || r.err
  return (
    <>
      <PageHeader title={IX.queues} screenId="A17.07" description={IX.queuesDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !q.data || !r.data ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <Stat label={IX.redisOps} value={String(r.data.ops_per_sec)} />
              <Stat label={IX.hitRate} value={`${r.data.hit_rate.toFixed(1)}%`} />
              <Stat label={IX.depth} value={q.data.media_jobs.toLocaleString()} tone={q.data.media_jobs > 0 ? 'warning' : undefined} />
            </div>
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.queueName}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.depth}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.status}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border-subtle">
                    <td className="px-4 py-2 font-mono text-primary">grom:media:jobs</td>
                    <td className="px-4 py-2 text-right tabular text-secondary">{q.data.media_jobs.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={`rounded px-1.5 py-0.5 text-xs ${q.data.media_jobs > 0 ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'}`}>
                        {q.data.media_jobs > 0 ? IX.mediaQueue : IX.empty}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.11 — Sekin so'rovlar (pg_stat_statements) ─────────────── */
export function InfraSlowQueriesScreen() {
  const { data, err, reload } = useLoad<SlowQueriesResp>(fetchSlowQueries)
  return (
    <>
      <PageHeader title={IX.slowQ} screenId="A17.11" description={IX.slowQDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows />
        ) : !data.available ? (
          <div className="mx-auto max-w-2xl">
            <EmptyState title={IX.slowQUnavailable} description={IX.slowQUnavailableDesc} />
          </div>
        ) : data.queries.length === 0 ? (
          <EmptyState title={IX.empty} />
        ) : (
          <div className="mx-auto max-w-5xl overflow-hidden rounded-lg border border-border-default">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-2 text-xs text-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">{IX.query}</th>
                  <th className="px-4 py-2 text-right font-medium">{IX.calls}</th>
                  <th className="px-4 py-2 text-right font-medium">{IX.meanMs}</th>
                  <th className="px-4 py-2 text-right font-medium">{IX.totalMs}</th>
                  <th className="px-4 py-2 text-right font-medium">{IX.rowsCol}</th>
                </tr>
              </thead>
              <tbody>
                {data.queries.map((s) => (
                  <tr key={s.query_id} className="border-t border-border-subtle align-top">
                    <td className="max-w-lg px-4 py-2 font-mono text-xs text-secondary">
                      <span className="line-clamp-2">{s.query}</span>
                    </td>
                    <td className="px-4 py-2 text-right tabular text-tertiary">{s.calls.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular text-primary">{s.mean_ms.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right tabular text-tertiary">{s.total_ms.toFixed(0)}</td>
                    <td className="px-4 py-2 text-right tabular text-tertiary">{s.rows.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.12 — API javob vaqtlari (/metrics) ────────────────────── */
export function InfraApiLatencyScreen() {
  const { data, err, reload } = useLoad<ApiMetrics>(fetchApiMetrics)
  return (
    <>
      <PageHeader title={IX.apiLatency} screenId="A17.12" description={IX.apiLatencyDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto max-w-4xl space-y-3">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
              <Stat label={IX.avgLatency} value={fmtMs(data.avg_latency_ms)} />
              <Stat label={IX.totalReq} value={data.total_requests.toLocaleString()} />
              <Stat label={IX.inFlight} value={String(data.in_flight)} />
              <Stat label={IX.errorRate} value={`${data.error_rate_pct.toFixed(2)}%`} tone={data.error_rate_pct > 1 ? 'danger' : 'success'} />
              <Stat label={IX.wsActive} value={data.ws_active.toLocaleString()} />
              <Stat label={IX.pushSent} value={data.push_sent.toLocaleString()} />
              <Stat label={IX.buildVersion} value={data.version} />
            </div>
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <p className="mb-3 text-sm font-medium text-primary">{IX.byClass}</p>
              <div className="grid grid-cols-4 gap-2.5">
                <Stat label="2xx" value={data.req_2xx.toLocaleString()} tone="success" />
                <Stat label="3xx" value={data.req_3xx.toLocaleString()} />
                <Stat label="4xx" value={data.req_4xx.toLocaleString()} tone={data.req_4xx > 0 ? 'warning' : undefined} />
                <Stat label="5xx" value={data.req_5xx.toLocaleString()} tone={data.req_5xx > 0 ? 'danger' : undefined} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
